import React, { useEffect, useState } from 'react';
import NumberFormat from 'react-number-format';
import { Link, useHistory } from 'react-router-dom';
import { Button, Container, Dimmer, Form, Loader, Table } from 'semantic-ui-react';
import { formatDate } from '../../../utils/shared.utils';
import { parsePaymentChannel, paymentChannelClassName } from '../../../constants/payment-channel';
import './order.report.scss';

function ReportOrdersBi(props: any) {

  const history = useHistory()
  const [orders, setOrders] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [errorMsg, setErrorMsg] = useState("")
  const [fetchedOrders, setFetchedOrders] = useState(false)
  const today = new Date();
  const monthAgo = new Date();
  monthAgo.setMonth(monthAgo.getMonth() - 1);
  const [fromDate, setFromDate] = useState<string>(formatDate(monthAgo))
  const [toDate, setToDate] = useState<string>(formatDate(today))
  const [totalOrderCount, setTotalOrderCount] = useState<number>(0)
  const [totalAmount, setTotalAmount] = useState<number>(0)
  const [totalDeliveryCost, setTotalDeliveryCost] = useState<number>(0)
  const [totalImportCost, setTotalImportCost] = useState<number>(0)
  const [totalStorageCost, setTotalStorageCost] = useState<number>(0)

  useEffect(() => {
    if (fetchedOrders) {
      return
    }
    // Use initial state values for first load only
    fetchOrders(fromDate, toDate);
    setFetchedOrders(true)
  }, []);

  const normaliseReportDateString = (date: string): string => {
    const parsedDate = new Date(date);
    if (!isNaN(parsedDate.getTime())) {
      return parsedDate.toISOString().slice(0, 10);
    }
    return date;
  }

  const fetchOrders = (fromDateParam?: string, toDateParam?: string) => {
    // Use provided parameters or current state values
    const currentFromDate = fromDateParam ?? fromDate;
    const currentToDate = toDateParam ?? toDate;
    
    var token = localStorage.getItem('adminToken')
    if (token && token !== 'undefined') {
      setLoading(true)
      // Reset totals while loading new data
      setTotalOrderCount(0)
      setTotalAmount(0)
      setTotalDeliveryCost(0)
      setTotalImportCost(0)
      setTotalStorageCost(0)

      const requestBody = { 
        startDate: currentFromDate, 
        endDate: currentToDate
      };
      
      console.log('Fetching orders with:', requestBody);
      
      fetch(process.env.REACT_APP_API_URL + 'mongol/report/ordersbi', {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Authorization': token,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      }).then((res: any) => {
        setLoading(false)
        if (res.ok) {
          res.json().then((resJson: any) => {
            const rawOrders: any[] = resJson.data || [];
            const totalsByOrder: Record<string | number, number> = {};
            const deliveryCostByOrder: Record<string | number, number> = {};
            const lastIndexByOrder: Record<string | number, number> = {};
            let sequentialOrderCount = 0;
            let previousOrderId: string | number | undefined;

            const productAmountByOrder: Record<string | number, number> = {};
            const orderDeliveryCost: Record<string | number, number> = {};
            const storageCostByOrder: Record<string | number, number> = {};

            rawOrders.forEach((order: any, index: number) => {
              const orderId = order?.order_id ?? index;
              const productAmount = Number(order?.product_total_amount) || 0;
              const itemDeliveryCost = (Number(order.productDeliveryCost) || 0) * (Number(order.order_product_count) || 0);
              const lineSt = Number(order?.storage_cost ?? order?.storageCost) || 0;
              productAmountByOrder[orderId] = (productAmountByOrder[orderId] || 0) + productAmount;
              // delivery_cost is order-level (same value on every row of the order) — only store it once
              if (!(orderId in orderDeliveryCost)) {
                orderDeliveryCost[orderId] = Number(order?.delivery_cost) || 0;
              }
              totalsByOrder[orderId] = productAmountByOrder[orderId] + (orderDeliveryCost[orderId] || 0);
              deliveryCostByOrder[orderId] = (deliveryCostByOrder[orderId] || 0) + itemDeliveryCost;
              storageCostByOrder[orderId] = (storageCostByOrder[orderId] || 0) + lineSt;
              lastIndexByOrder[orderId] = index;
              if (orderId !== previousOrderId) {
                sequentialOrderCount += 1;
                previousOrderId = orderId;
              }
            });

            let computedTotalAmount = 0;
            let computedTotalDeliveryCost = 0;
            let computedTotalImportCost = 0;
            let computedTotalStorageCost = 0;
            const orders: any[] = rawOrders.map((order: any, index: number) => {
              const normalisedOrder = { ...order };
              const orderId = normalisedOrder?.order_id ?? index;

              if (normalisedOrder.order_date) {
               normalisedOrder.order_date = normaliseReportDateString(normalisedOrder.order_date);
              }
              if (normalisedOrder.delivery_date) {
                normalisedOrder.delivery_date = normaliseReportDateString(normalisedOrder.delivery_date);
              }

              // Calculate delivery cost for this item (multiply by count)
              const itemDeliveryCost = (Number(normalisedOrder.productDeliveryCost) || 0) * (Number(normalisedOrder.order_product_count) || 0);
              computedTotalDeliveryCost += itemDeliveryCost;

              // Import cost total for this line
              const itemImportCost = Number(normalisedOrder.import_cost_total) || 0;
              computedTotalImportCost += itemImportCost;

              const lineStorageCost = Number(
                normalisedOrder.storage_cost ?? (normalisedOrder as any).storageCost
              ) || 0;
              computedTotalStorageCost += lineStorageCost;

              const isLastRowForOrder = index === lastIndexByOrder[orderId];
              normalisedOrder.receipt_total_amount = isLastRowForOrder ? totalsByOrder[orderId] : 0;
              normalisedOrder.delivery_cost_total_amount = isLastRowForOrder ? deliveryCostByOrder[orderId] : 0;
              normalisedOrder.storage_cost_total_amount = isLastRowForOrder ? storageCostByOrder[orderId] : 0;
              if (isLastRowForOrder) {
                computedTotalAmount += totalsByOrder[orderId];
              }

              // Add the calculated item delivery cost to the order object for display
              normalisedOrder.itemDeliveryCost = itemDeliveryCost;

              return normalisedOrder;
            });
            
            // Update all states together
            setOrders(orders)
            setTotalOrderCount(sequentialOrderCount)
            setTotalAmount(computedTotalAmount)
            setTotalDeliveryCost(computedTotalDeliveryCost)
            setTotalImportCost(computedTotalImportCost)
            setTotalStorageCost(computedTotalStorageCost)
          })
        } else {
          // If response is not ok, reset totals
          setTotalOrderCount(0)
          setTotalAmount(0)
          setTotalDeliveryCost(0)
          setTotalImportCost(0)
          setTotalStorageCost(0)
        }
      }).catch(err => {
        setLoading(false)
        setErrorMsg("Сүлжээний алдаа")
        setTotalOrderCount(0)
        setTotalAmount(0)
        setTotalDeliveryCost(0)
        setTotalImportCost(0)
        setTotalStorageCost(0)
        console.error(err)
      })
    }
    else {
      window.alert("Та дахин нэвтэрнэ үү")
      history.push("/mongol/login")
    }
  }

  const convertPaymentMethod = (method: string | undefined | null): string => {
    if (!method) return '';
    const methodMap: Record<string, string> = {
      'card': 'карт',
      'load': 'зээл',
      'account': 'данс',
      'cash': 'бэлэн'
    };
    return methodMap[method.toLowerCase()] || method;
  }

    const onFilterApply = () => {
    setTotalOrderCount(0)
    setTotalAmount(0)
    setTotalDeliveryCost(0)
    setTotalImportCost(0)
    setTotalStorageCost(0)
    // Explicitly pass current state values to ensure we use the latest dates
    fetchOrders(fromDate, toDate)
  }

  const downloadCsv = () => {
    if (!orders.length) {
      return;
    }

    const headers = [
      '№',
      'Захиалгын дугаар',
      'Захиалсан огноо',
      'Хүргэлтийн огноо',
      'Компани нэр',
      'Бүтээгдэхүүний нэр',
      'Төлбөр төлөлт',
      'Худалдан авсан сувгийн төлөв',
      'Нийлүүлэгч',
      'Бараа борлуулсан хүн',
      'Борлуулатын суваг',
      'Тоо',
      'Нэгж үнэ',
      'Нийт үнэ',
      'Падааны дүн',
      'Нэгж өртөг',
      'Нийт өртөг',
      'Хадгалалтын зардал',
      'Нийт хадгалалтын зардал',
      'Нэгж хүргэлтийн зардал',
      'Хүргэлтийн зардал',
      'Хүргэлтийн нийт зардал'
    ];

    const escapeCsvValue = (value: any) => {
      if (value === null || value === undefined) {
        return '';
      }
      const stringValue = String(value).replace(/"/g, '""');
      if (stringValue.includes(',') || stringValue.includes('\n') || stringValue.includes('"')) {
        return `"${stringValue}"`;
      }
      return stringValue;
    };

    const rows = orders.map((order: any, index: number) => ([
      index + 1,
      order.order_token || '',
      order.order_date || '',
      order.delivery_date || '',
      order.company_name || '',
      order.product_name || '',
      order.pay_later_flag ? 'дараа төлбөр' : convertPaymentMethod(order.order_method),
      parsePaymentChannel(order.order_payment_channel),
      order.provider_name || '',
      order.order_client_name || '',
      order.order_store_flag === 1 ? 'дэлгүүр' : 'веб',
      order.order_product_count || '',
      order.product_price || '',
      order.product_total_amount || '',
      order.receipt_total_amount || '',
      order.import_cost_per_unit || '',
      order.import_cost_total || '',
      order.storage_cost ?? (order as any).storageCost ?? '',
      order.storage_cost_total_amount ?? '',
      order.productDeliveryCost || '',
      order.itemDeliveryCost || '',
      order.delivery_cost_total_amount || '',
    ].map(escapeCsvValue).join(',')));

    // Prepend UTF-8 BOM so applications like Excel correctly detect encoding
    const csvContent = [headers.join(','), ...rows].join('\n');
    const bom = '\uFEFF';
    const blob = new Blob([bom + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `orders-bi-${fromDate}-${toDate}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  return (
    <div className="page-admin">
      <Container>
        <div className="flex-div width-100">
          <Dimmer active={loading} inverted>
            <Loader size='medium'></Loader>
          </Dimmer>
          <section className="admin-reports" id="my-form">

            <div className="width-100 text-center"><h2>Захиалгын тайлан</h2></div>

            <Form className="report-filter flex-div margin-top-50">
              <Form.Input label="Эхний өдөр: " type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)}/>
              <Form.Input label="Сүүлийн өдөр: " type="date" value={toDate} onChange={(e) => setToDate(e.target.value)}/>
              <Form.Field className="report-filter-actions">
                <label>&nbsp;</label>
                <Button type="button" onClick={onFilterApply}>Хайх</Button>
              </Form.Field>
              <Form.Field className="report-filter-actions">
                <label>&nbsp;</label>
                <Button type="button" onClick={downloadCsv} disabled={!orders.length}>CSV татах</Button>
              </Form.Field>
            </Form>

            <div className="error-msg">{ errorMsg }</div>

            <div className="order-summary-section flex-div justify-content-center align-items-center" key={`summary-top-${totalAmount}-${totalOrderCount}-${totalDeliveryCost}-${totalImportCost}-${totalStorageCost}`}>
              <div className="order-summary-content flex-div align-items-center">
                <div className="summary-item">
                  <strong>Захиалгын тоо: </strong>
                  <span>{totalOrderCount.toLocaleString()}</span>
                </div>
                <div className="summary-item">
                  <strong>Нийт дүн: </strong>
                  <NumberFormat displayType="text" value={totalAmount} suffix="₮" thousandSeparator={true} key={`total-top-${totalAmount}`} />
                </div>
                <div className="summary-item">
                  <strong>Нийт өртөг: </strong>
                  <NumberFormat displayType="text" value={totalImportCost} suffix="₮" thousandSeparator={true} key={`import-top-${totalImportCost}`} />
                </div>
                <div className="summary-item">
                  <strong>Нийт хүргэлтийн зардал: </strong>
                  <NumberFormat displayType="text" value={totalDeliveryCost} suffix="₮" thousandSeparator={true} key={`delivery-top-${totalDeliveryCost}`} />
                </div>
                <div className="summary-item">
                  <strong>Нийт хадгалалтын зардал: </strong>
                  <NumberFormat displayType="text" value={totalStorageCost} suffix="₮" thousandSeparator={true} key={`storage-top-${totalStorageCost}`} />
                </div>
              </div>
            </div>

            <div className="orders-bi-table-scroll">
              <Table striped className="admin-list-table" width={16}>
                <Table.Header>
                  <Table.Row>
                    <Table.HeaderCell>№</Table.HeaderCell>
                    <Table.HeaderCell>Захиалгын дугаар</Table.HeaderCell>
                    <Table.HeaderCell>Захиалсан огноо</Table.HeaderCell>
                    <Table.HeaderCell>Хүргэлтийн огноо</Table.HeaderCell>
                    <Table.HeaderCell>Компани нэр</Table.HeaderCell>
                    <Table.HeaderCell>Бүтээгдэхүүний нэр</Table.HeaderCell>
                    <Table.HeaderCell>Төлбөр төлөлт</Table.HeaderCell>
                    <Table.HeaderCell>Худалдан авсан сувгийн төлөв</Table.HeaderCell>
                    <Table.HeaderCell>Нийлүүлэгч</Table.HeaderCell>
                    <Table.HeaderCell>Бараа борлуулсан хүн</Table.HeaderCell>
                    <Table.HeaderCell>Борлуулатын суваг</Table.HeaderCell>
                    <Table.HeaderCell>Тоо</Table.HeaderCell>
                    <Table.HeaderCell>Нэгж үнэ</Table.HeaderCell>
                    <Table.HeaderCell>Нийт үнэ</Table.HeaderCell>
                    <Table.HeaderCell>Падааны дүн</Table.HeaderCell>
                    <Table.HeaderCell>Нэгж өртөг</Table.HeaderCell>
                    <Table.HeaderCell>Нийт өртөг</Table.HeaderCell>
                    <Table.HeaderCell>Хадгалалтын зардал</Table.HeaderCell>
                    <Table.HeaderCell>Нийт хадгалалтын зардал</Table.HeaderCell>
                    <Table.HeaderCell>Нэгж хүргэлтийн зардал</Table.HeaderCell>
                    <Table.HeaderCell>Хүргэлтийн зардал</Table.HeaderCell>
                    <Table.HeaderCell>Хүргэлтийн нийт зардал</Table.HeaderCell>
                  </Table.Row>
                </Table.Header>
              
                <Table.Body>
                  {orders?.map((order: any, index: number) => {
                    const prevOrder = index > 0 ? orders[index - 1] : null;
                    const isFirstAllocRow = !prevOrder || prevOrder.order_item_id !== order.order_item_id;
                    const lineStorageCost = Number(order.storage_cost ?? (order as any).storageCost) || 0;
                    return <React.Fragment key={"order-" + index}>
                      <Table.Row
                        style={{
                          backgroundColor: isFirstAllocRow
                            ? 'var(--admin-table-row-muted)'
                            : 'var(--admin-table-row-alt)',
                        }}
                      >
                        <Table.Cell data-label="№">
                        {isFirstAllocRow
                          ? <Link to={"/mongol/report/order/" + order.order_id} target="_blank">{index+1}</Link>
                          : ''}
                      </Table.Cell>
                      <Table.Cell data-label="Захиалгын дугаар">{isFirstAllocRow ? order.order_token : ''}</Table.Cell>
                      <Table.Cell data-label="Захиалсан огноо">{isFirstAllocRow ? order.order_date : ''}</Table.Cell>
                      <Table.Cell data-label="Хүргэлтийн огноо">{isFirstAllocRow ? order.delivery_date : ''}</Table.Cell>
                      <Table.Cell data-label="Компани нэр">
                        {isFirstAllocRow
                          ? <Link className="underline" to={"/mongol/company/" + order.company_id} target="_blank">{ order.company_name }</Link>
                          : ''}
                      </Table.Cell>
                      <Table.Cell data-label="Бүтээгдэхүүний нэр">{isFirstAllocRow ? order.product_name : ''}</Table.Cell>
                      <Table.Cell data-label="Төлбөр төлөлт">{isFirstAllocRow ? (order.pay_later_flag ? 'дараа төлбөр' : convertPaymentMethod(order.order_method)) : ''}</Table.Cell>
                      <Table.Cell data-label="Худалдан авсан сувгийн төлөв">
                        {isFirstAllocRow
                          ? <span className={paymentChannelClassName(order.order_payment_channel)}>{parsePaymentChannel(order.order_payment_channel)}</span>
                          : ''}
                      </Table.Cell>
                      <Table.Cell data-label="Нийлүүлэгч">{isFirstAllocRow ? order.provider_name : ''}</Table.Cell>
                      <Table.Cell data-label="Бараа борлуулсан хүн">
                        {isFirstAllocRow
                          ? <Link className="underline" to={"/mongol/client/" + order.client_id} target="_blank">{ order.order_client_name }</Link>
                          : ''}
                      </Table.Cell>
                      <Table.Cell data-label="Борлуулатын суваг">{isFirstAllocRow ? (order.order_store_flag === 1 ? 'дэлгүүр' : 'веб') : ''}</Table.Cell>
                      <Table.Cell data-label="Тоо">{order.order_product_count}</Table.Cell>
                      <Table.Cell data-label="Нэгж үнэ">
                        {isFirstAllocRow
                          ? <NumberFormat displayType="text" value={order.product_price} suffix="₮" thousandSeparator={true} />
                          : ''}
                      </Table.Cell>
                      <Table.Cell data-label="Нийт үнэ">
                        <NumberFormat displayType="text" value={order.product_total_amount} suffix="₮" thousandSeparator={true} />
                      </Table.Cell>
                      <Table.Cell data-label="Падааны дүн">
                        {order.receipt_total_amount ? <NumberFormat displayType="text" value={order.receipt_total_amount} suffix="₮" thousandSeparator={true} /> : ''}
                      </Table.Cell>
                      <Table.Cell data-label="Нэгж өртөг">
                        {order.import_cost_per_unit != null
                          ? <NumberFormat displayType="text" value={Number(order.import_cost_per_unit)} suffix="₮" thousandSeparator={true} decimalScale={2} />
                          : '-'}
                      </Table.Cell>
                      <Table.Cell data-label="Нийт өртөг">
                        {order.import_cost_total != null && Number(order.import_cost_total) > 0
                          ? <NumberFormat displayType="text" value={Number(order.import_cost_total)} suffix="₮" thousandSeparator={true} decimalScale={2} />
                          : '-'}
                      </Table.Cell>
                      <Table.Cell data-label="Хадгалалтын зардал">
                        {lineStorageCost > 0
                          ? <NumberFormat displayType="text" value={lineStorageCost} suffix="₮" thousandSeparator={true} decimalScale={2} />
                          : '-'}
                      </Table.Cell>
                      <Table.Cell data-label="Нийт хадгалалтын зардал">
                        {order.storage_cost_total_amount
                          ? <NumberFormat displayType="text" value={order.storage_cost_total_amount} suffix="₮" thousandSeparator={true} decimalScale={2} />
                          : ''}
                      </Table.Cell>
                      <Table.Cell data-label="Нэгж хүргэлтийн зардал">
                        {isFirstAllocRow
                          ? <NumberFormat displayType='text' value={order.productDeliveryCost} suffix='₮' thousandSeparator={true} />
                          : ''}
                      </Table.Cell>
                      <Table.Cell data-label="Хүргэлтийн зардал">
                        {isFirstAllocRow
                          ? <NumberFormat displayType='text' value={order.itemDeliveryCost} suffix='₮' thousandSeparator={true} />
                          : ''}
                      </Table.Cell>
                      <Table.Cell data-label="Хүргэлтийн нийт зардал">
                        {order.delivery_cost_total_amount ? <NumberFormat displayType="text" value={order.delivery_cost_total_amount} suffix="₮" thousandSeparator={true} /> : ''}
                      </Table.Cell>
                    </Table.Row>
                  </React.Fragment>
                })}
              </Table.Body>
            </Table>
            </div>

            <div className="order-summary-section flex-div justify-content-center align-items-center" key={`summary-${totalAmount}-${totalOrderCount}-${totalDeliveryCost}-${totalImportCost}-${totalStorageCost}`}>
              <div className="order-summary-content flex-div align-items-center">
                <div className="summary-item">
                  <strong>Захиалгын тоо: </strong>
                  <span>{totalOrderCount.toLocaleString()}</span>
                </div>
                <div className="summary-item">
                  <strong>Нийт дүн: </strong>
                  <NumberFormat displayType="text" value={totalAmount} suffix="₮" thousandSeparator={true} key={`total-${totalAmount}`} />
                </div>
                <div className="summary-item">
                  <strong>Нийт өртөг: </strong>
                  <NumberFormat displayType="text" value={totalImportCost} suffix="₮" thousandSeparator={true} key={`import-${totalImportCost}`} />
                </div>
                <div className="summary-item">
                  <strong>Нийт хүргэлтийн зардал: </strong>
                  <NumberFormat displayType="text" value={totalDeliveryCost} suffix="₮" thousandSeparator={true} key={`delivery-${totalDeliveryCost}`} />
                </div>
                <div className="summary-item">
                  <strong>Нийт хадгалалтын зардал: </strong>
                  <NumberFormat displayType="text" value={totalStorageCost} suffix="₮" thousandSeparator={true} key={`storage-${totalStorageCost}`} />
                </div>
              </div>
            </div>
          </section>
        </div>
      </Container>
    </div>
  );
}

export default ReportOrdersBi;